import React from 'react';
import type { SelectMenuItem } from './__commons__/selectCommons';
import { itemContainerClass, contentContainerClass } from './__commons__/selectCommons';
import { Text } from '../text';
import { IconLoader } from '../iconLoader';
import { Tag } from '../tag';
import { CheckIndicator } from '../check-indicator';

export interface SelectItemContentProps {
    item: SelectMenuItem;
    multiSelect: boolean;
    displaySelectedIcon: boolean;
    enableActions?: boolean;
}

const actionButtonClass =
    'ml-2 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded p-0 border-0 bg-transparent text-system-on-surface-variant ' +
    'primary-hover-state focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-primary';
const pressedActionClass = '!bg-system-on-surface';
const actionIconClass = 'stroke-system-on-surface-variant fill-system-on-surface-variant';
const pressedActionIconClass = 'stroke-system-surface fill-system-surface';

export const SelectItemContent: React.FC<SelectItemContentProps> = ({
    item,
    multiSelect,
    displaySelectedIcon,
    enableActions = false,
}) => {
    const Icon = item.icon;
    return (
        <div className={itemContainerClass}>
            {/* Decorative: the row carries the checked state. */}
            {multiSelect && <CheckIndicator id={`${item.id}-cb`} checked={item.isSelected} className="mr-2" />}
            {(item.iconName || Icon) && (
                <span className="flex flex-shrink-0 items-center">
                    {item.iconName ? (
                        <IconLoader size="sm" iconName={item.iconName} />
                    ) : (
                        Icon && <Icon className="h-4 w-4" aria-hidden="true" />
                    )}
                </span>
            )}
            <div className={contentContainerClass}>
                <Text variant="label-large">{item.value}</Text>
                {item.label && (
                    <Text variant="body-small" className="text-system-on-surface-variant">
                        {item.label}
                    </Text>
                )}
                {item.tags && item.tags.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                        {item.tags.map((tag, index) => (
                            <Tag
                                key={tag.id ?? `${item.id}-tag-${index}`}
                                size="sm"
                                {...tag}
                                hoverable={false}
                                className={`!py-0.5 ${tag.className ?? ''}`}
                            />
                        ))}
                    </div>
                )}
            </div>
            {!multiSelect && displaySelectedIcon && item.isSelected && (
                <IconLoader size="sm" iconName="IconCheck" data-testid={`${item.id}-checkmark`} />
            )}
            {enableActions &&
                item.actionIcon &&
                (item.onAction ? (
                    <button
                        type="button"
                        tabIndex={-1}
                        aria-label={`${item.actionLabel ?? 'Action'} ${item.value}`.trim()}
                        aria-pressed={item.actionPressed}
                        className={`${actionButtonClass} ${item.actionPressed ? pressedActionClass : ''}`}
                        data-testid={item.testId ? `${item.testId}-action` : undefined}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                            e.stopPropagation();
                            item.onAction?.(item);
                        }}
                    >
                        <IconLoader
                            size="sm"
                            iconName={item.actionIcon}
                            className={item.actionPressed ? pressedActionIconClass : actionIconClass}
                        />
                    </button>
                ) : (
                    <span className={actionButtonClass} aria-hidden="true">
                        <IconLoader
                            size="sm"
                            iconName={item.actionIcon}
                            className={item.actionPressed ? pressedActionIconClass : actionIconClass}
                        />
                    </span>
                ))}
        </div>
    );
};
